import { WidePageLayout } from '~/components/PageLayout'
import styled from '@emotion/styled'
import { color, device, fontWeight, H3, space } from '@ticketswap/solar'
import { useTranslation } from '~/components/TranslationContext'
import Link from 'next/link'
import { ChevronRight } from '@ticketswap/solar/icons'
import { RouteMap } from '~/lib/routeMap'
import getConfig from 'next/config'

const { publicRuntimeConfig } = getConfig()

const Container = styled.div`
  border-block-start: 1px solid ${color.stroke};
`

const Layout = styled(WidePageLayout)`
  display: flex;
  gap: ${space[24]};
  padding-block-start: ${space[40]};
  padding-block-end: ${space[40]};

  @media ${device.tablet} {
    gap: ${space[56]};
  }
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-shrink: 0;
`

const ImagesContainer = styled.div`
  display: flex;
  align-items: center;
  flex-basis: 100%;
  justify-content: space-between;
  overflow-x: auto;
  scroll-snap-type: x mandatory;

  -ms-overflow-style: none; /* Hide scrollbar for IE and Edge */
  scrollbar-width: none; /* Hide scrollbar for Firefox */

  /* Hide scrollbar for Chrome, Safari and Opera */
  ::-webkit-scrollbar {
    display: none;
  }
`

const BecomePartnerTitle = styled(H3)`
  font-weight: ${fontWeight.semiBold};
`

const BecomePartnerLink = styled.a`
  font-weight: ${fontWeight.semiBold};
`

const ImageBlock = styled.img`
  max-height: ${space[56]};
  display: ${props => (props.isDark ? 'none' : 'block')};
  scroll-snap-align: start;

  :not(:last-of-type) {
    margin-inline-end: ${space[8]};
  }

  [data-theme='dark'],
  [data-theme='ade'] & {
    display: ${props => (props.isDark ? 'block' : 'none')};
  }
`

const PartnerBanner = () => {
  const { t } = useTranslation()
  const images = [
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/defqon.png`,
      alt: `Defqon ${t('common:generic.logo')}`,
      width: 72,
      height: 56,
    },
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/eventix.png`,
      alt: `SeeTickets ${t('common:generic.logo')}`,
      width: 134,
      height: 30,
    },
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/sziget.png`,
      alt: `Sziget ${t('common:generic.logo')}`,
      width: 72,
      height: 56,
    },
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/hellfest.png`,
      alt: `Hellfest ${t('common:generic.logo')}`,
      width: 72,
      height: 56,
    },
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/ab.png`,
      alt: `Ancienne Belgique ${t('common:generic.logo')}`,
      width: 72,
      height: 56,
    },
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/dgtl.png`,
      alt: `DGTL ${t('common:generic.logo')}`,
      width: 82,
      height: 56,
    },
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/cercle.png`,
      alt: `Cercle ${t('common:generic.logo')}`,
      width: 72,
      height: 56,
    },
  ]

  const imagesDark = [
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/defqon-dark.png`,
      alt: `Defqon ${t('common:generic.logo')}`,
      width: 72,
      height: 56,
    },
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/eventix-dark.png`,
      alt: `SeeTickets ${t('common:generic.logo')}`,
      width: 134,
      height: 30,
    },
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/sziget.png`,
      alt: `Sziget ${t('common:generic.logo')}`,
      width: 72,
      height: 56,
    },
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/hellfest-dark.png`,
      alt: `Hellfest ${t('common:generic.logo')}`,
      width: 72,
      height: 56,
    },
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/ab-dark.png`,
      alt: `Ancienne Belgique ${t('common:generic.logo')}`,
      width: 72,
      height: 56,
    },
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/dgtl-dark.png`,
      alt: `DGTL ${t('common:generic.logo')}`,
      width: 82,
      height: 56,
    },
    {
      src: `${publicRuntimeConfig.NEXT_PUBLIC_CDN_URL}/static/images/partners/logos/cercle-dark.png`,
      alt: `Cercle ${t('common:generic.logo')}`,
      width: 72,
      height: 56,
    },
  ]

  return (
    <Container>
      <Layout>
        <TextWrapper>
          <BecomePartnerTitle>{t('common:footer.partnerBanner.title')}</BecomePartnerTitle>
          <Link prefetch={false} href={RouteMap['PARTNERS']} passHref>
            <BecomePartnerLink>
              {t('common:footer.partnerBanner.link')}
              <ChevronRight size={16} />
            </BecomePartnerLink>
          </Link>
        </TextWrapper>
        <ImagesContainer>
          {imagesDark.map((image, index) => (
            <ImageBlock
              isDark={true}
              key={`parter-logo-${index}`}
              src={image.src}
              alt={image.alt}
              width={image.width}
              height={image.height}
              loading="lazy"
            />
          ))}

          {images.map((image, index) => (
            <ImageBlock
              isDark={false}
              key={`parter-logo-${index}`}
              src={image.src}
              alt={image.alt}
              width={image.width}
              height={image.height}
              loading="lazy"
            />
          ))}
        </ImagesContainer>
      </Layout>
    </Container>
  )
}

export default PartnerBanner
